<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      title="运营端 · 运单巡检。待抢/待接超过 5 分钟进入「卡住待办」。可强制指派、回自由池、取消（记审计）。帮我买/送的清单或物品在「清单/物品」列与详情中查看。造单用「模拟造单」。"
    />

    <el-tabs v-model="tab" class="mb-12px" @tab-change="onTabChange">
      <el-tab-pane label="全部运单" name="all" />
      <el-tab-pane name="todos">
        <template #label>
          <span>
            卡住待办
            <el-badge v-if="todosTotal" :value="todosTotal" class="ml-4px" />
          </span>
        </template>
      </el-tab-pane>
    </el-tabs>

    <el-form v-if="tab === 'all'" :inline="true" class="mb-12px" @submit.prevent>
      <el-form-item label="关键词">
        <el-input
          v-model="filters.keyword"
          clearable
          placeholder="运单号 / 外部单号 / 骑手手机"
          class="!w-220px"
        />
      </el-form-item>
      <el-form-item label="城市">
        <el-select v-model="filters.city" clearable placeholder="全部" class="!w-120px">
          <el-option v-for="c in cityOptions" :key="c" :label="c" :value="c" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="filters.status" clearable placeholder="全部" class="!w-140px">
          <el-option
            v-for="s in statusFilterOptions"
            :key="s.value"
            :label="s.label"
            :value="s.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="策略">
        <el-select v-model="filters.strategy" clearable placeholder="全部" class="!w-140px">
          <el-option label="自由抢单" value="free_pool" />
          <el-option label="机构派单" value="org_dispatch" />
        </el-select>
      </el-form-item>
      <el-form-item label="机构">
        <el-input v-model="filters.orgId" clearable placeholder="orgId" class="!w-120px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">刷新</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </el-form-item>
    </el-form>
    <div v-else class="mb-12px">
      <el-button type="primary" @click="loadTodos">刷新待办</el-button>
    </div>

    <el-table :data="displayList" size="small" border>
      <el-table-column prop="waybillId" label="运单号" min-width="140" />
      <el-table-column prop="externalId" label="外部单号" min-width="120" />
      <el-table-column prop="city" label="城市" width="80" />
      <el-table-column label="策略" width="110" align="center">
        <template #default="{ row }">
          <el-tag size="small" type="info">{{ waybillStrategyLabel(row.strategy) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="品类" width="100">
        <template #default="{ row }">{{ bizTypeLabel(row.bizType) }}</template>
      </el-table-column>
      <el-table-column label="状态" width="120" align="center">
        <template #default="{ row }">
          <el-tag :type="waybillStatusTagType(row.status)" size="small">
            {{ waybillStatusLabel(row.status, row.bizType) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="riderPhone" label="骑手" width="120" />
      <el-table-column label="运费" width="90" align="right">
        <template #default="{ row }">
          {{ formatMoneyAmount(row.fee, row.currency) }}
        </template>
      </el-table-column>
      <el-table-column prop="orgId" label="机构" width="90" />
      <el-table-column label="清单/物品" min-width="160" show-overflow-tooltip>
        <template #default="{ row }">{{ remarkCell(row) }}</template>
      </el-table-column>
      <el-table-column label="溢出" width="70">
        <template #default="{ row }">{{ row.overflowed ? '是' : '—' }}</template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <el-button
            v-if="canDispose(row)"
            link
            type="success"
            @click="onForceAssign(row)"
          >
            指派
          </el-button>
          <el-button
            v-if="canReturnPool(row)"
            link
            type="warning"
            @click="onReturnPool(row)"
          >
            回池
          </el-button>
          <el-button
            v-if="canCancel(row)"
            link
            type="danger"
            @click="onCancel(row)"
          >
            取消
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <Pagination
      v-if="tab === 'all'"
      :total="listTotal"
      v-model:page="pageNo"
      v-model:limit="pageSize"
      @pagination="load"
    />
    <Pagination
      v-else
      :total="todosTotal"
      v-model:page="todosPageNo"
      v-model:limit="todosPageSize"
      @pagination="loadTodos"
    />

    <el-drawer v-model="detailVisible" title="运单详情 / 处置" size="480px">
      <template v-if="detail">
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="运单号">{{ detail.waybillId }}</el-descriptions-item>
          <el-descriptions-item label="外部单号">{{ detail.externalId }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="waybillStatusTagType(detail.status)" size="small">
              {{ waybillStatusLabel(detail.status, detail.bizType) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="策略">
            {{ waybillStrategyLabel(detail.strategy) }}
          </el-descriptions-item>
          <el-descriptions-item label="城市">{{ detail.city }}</el-descriptions-item>
          <el-descriptions-item label="机构">{{ detail.orgId || '—' }}</el-descriptions-item>
          <el-descriptions-item label="骑手">{{ detail.riderPhone || '—' }}</el-descriptions-item>
          <el-descriptions-item label="运费">
            {{ formatMoneyAmount(detail.fee, detail.currency) }}
          </el-descriptions-item>
          <el-descriptions-item label="品类">{{ bizTypeLabel(detail.bizType) }}</el-descriptions-item>
          <el-descriptions-item :label="pickLabel(detail.bizType)">
            <div>{{ detail.pickTitle || '—' }}</div>
            <div v-if="detail.pickAddr" class="text-12px text-[var(--el-text-color-secondary)] mt-2px">
              {{ detail.pickAddr }}
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="送点">
            <div>{{ detail.dropTitle || '—' }}</div>
            <div v-if="detail.dropAddr" class="text-12px text-[var(--el-text-color-secondary)] mt-2px">
              {{ detail.dropAddr }}
            </div>
          </el-descriptions-item>
          <el-descriptions-item :label="remarkLabel(detail.bizType)">
            <div class="whitespace-pre-wrap break-words">{{ detail.remark?.trim() || '—' }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="溢出">{{ detail.overflowed ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="抢单时间">
            {{ formatCapacityDateTime(detail.grabbedAt) }}
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">
            {{ formatCapacityDateTime(detail.createdAt) }}
          </el-descriptions-item>
        </el-descriptions>
        <div class="mt-16px flex flex-wrap gap-8px">
          <el-button v-if="canDispose(detail)" type="success" @click="onForceAssign(detail)">
            强制指派
          </el-button>
          <el-button v-if="canReturnPool(detail)" type="warning" @click="onReturnPool(detail)">
            回自由池
          </el-button>
          <el-button v-if="canCancel(detail)" type="danger" @click="onCancel(detail)">
            取消运单
          </el-button>
          <el-button type="danger" plain @click="onCompensate(detail)">人工赔付</el-button>
        </div>
        <div class="mt-16px">
          <div class="mb-8px text-14px font-600">完成图</div>
          <div v-if="photos.length" class="flex flex-wrap gap-8px">
            <el-image
              v-for="(url, i) in photos"
              :key="i"
              :src="url"
              :preview-src-list="photos"
              :initial-index="i"
              fit="cover"
              class="h-88px w-88px rounded"
            />
          </div>
          <el-empty v-else description="暂无完成图" :image-size="64" />
        </div>
      </template>
    </el-drawer>
  </ContentWrap>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router'
import * as WaybillApi from '@/api/capacity/waybill'
import * as CompensateApi from '@/api/capacity/compensate'
import {
  formatCapacityDateTime,
  formatMoneyAmount,
  waybillStatusLabel,
  waybillStatusTagType,
  waybillStrategyLabel,
  WAYBILL_STATUS_FILTER_OPTIONS
} from '@/api/capacity/labels'
import { ContentWrap } from '@/components/ContentWrap'
import { ElMessage, ElMessageBox } from 'element-plus'

defineOptions({ name: 'CapacityWaybillInspect' })

const route = useRoute()
const router = useRouter()
const tab = ref('all')
const list = ref<WaybillApi.CapacityWaybillItem[]>([])
const listTotal = ref(0)
const pageNo = ref(1)
const pageSize = ref(10)
const todos = ref<WaybillApi.CapacityWaybillItem[]>([])
const todosTotal = ref(0)
const todosPageNo = ref(1)
const todosPageSize = ref(10)
const filters = reactive({
  keyword: '',
  city: '',
  status: '',
  strategy: '',
  orgId: ''
})
const detailVisible = ref(false)
const detail = ref<WaybillApi.CapacityWaybillItem>()

const cityOptions = computed(() =>
  [...new Set(list.value.map((i) => i.city).filter(Boolean))].sort()
)
const statusFilterOptions = WAYBILL_STATUS_FILTER_OPTIONS

const displayList = computed(() => (tab.value === 'todos' ? todos.value : list.value))
const photos = computed(() => detail.value?.completionPhotoUrls?.filter(Boolean) || [])

const BIZ_LABEL: Record<string, string> = {
  food: '餐饮',
  city_delivery: '同城配送',
  express: '快递',
  fresh: '生鲜',
  pharmacy: '医药',
  grocery: '商超',
  errand_buy: '帮我买',
  errand_send: '帮我送',
}

function bizTypeLabel(bizType?: string) {
  if (!bizType) return '—'
  const k = bizType.toLowerCase()
  return BIZ_LABEL[k] || bizType
}

function isErrandBuy(bizType?: string) {
  return (bizType || '').toLowerCase() === 'errand_buy'
}

function isErrandSend(bizType?: string) {
  return (bizType || '').toLowerCase() === 'errand_send'
}

function pickLabel(bizType?: string) {
  return isErrandBuy(bizType) ? '购买点' : '取点'
}

function remarkLabel(bizType?: string) {
  if (isErrandBuy(bizType)) return '购物清单'
  if (isErrandSend(bizType)) return '物品描述'
  return '备注'
}

/** 列表格：跑腿优先展示清单/物品；其它有 remark 也展示 */
function remarkCell(row: WaybillApi.CapacityWaybillItem) {
  const t = (row.remark || '').trim()
  if (t) return t
  if (isErrandBuy(row.bizType) || isErrandSend(row.bizType)) return '（未填）'
  return '—'
}

const isTerminal = (row: WaybillApi.CapacityWaybillItem) =>
  row.status === 'completed' || row.status === 'cancelled'

const canDispose = (row: WaybillApi.CapacityWaybillItem) =>
  row.status === 'accepted' && !row.riderPhone

const canReturnPool = (row: WaybillApi.CapacityWaybillItem) => {
  if (isTerminal(row)) return false
  if (row.status === 'picked' || row.status === 'delivering') return false
  if (row.strategy === 'free_pool' && row.status === 'accepted' && !row.riderPhone) return false
  return true
}

const canCancel = (row: WaybillApi.CapacityWaybillItem) => !isTerminal(row)

const search = () => {
  pageNo.value = 1
  load()
}

const resetFilters = () => {
  filters.keyword = ''
  filters.city = ''
  filters.status = ''
  filters.strategy = ''
  filters.orgId = ''
  pageNo.value = 1
  load()
}

const openDetail = (row: WaybillApi.CapacityWaybillItem) => {
  detail.value = row
  detailVisible.value = true
}

const refreshAfterAct = async () => {
  await Promise.all([load(), loadTodos()])
  if (detail.value) {
    const all = [...list.value, ...todos.value]
    detail.value = all.find((i) => i.waybillId === detail.value?.waybillId) || detail.value
  }
}

const onForceAssign = async (row: WaybillApi.CapacityWaybillItem) => {
  const { value } = await ElMessageBox.prompt('目标骑手手机号', '强制指派', {
    inputPattern: /^1\d{10}$/,
    inputErrorMessage: '请输入 11 位手机号'
  })
  const r = await WaybillApi.waybillOpsAct({
    action: 'FORCE_ASSIGN',
    waybillId: row.waybillId,
    toPhone: value
  })
  if (!r.success) {
    ElMessage.error(r.message || '指派失败')
    return
  }
  ElMessage.success('已强制指派')
  await refreshAfterAct()
}

const onReturnPool = async (row: WaybillApi.CapacityWaybillItem) => {
  await ElMessageBox.confirm(`将运单 ${row.waybillId} 退回自由池？`, '回池确认', {
    type: 'warning'
  })
  const r = await WaybillApi.waybillOpsAct({ action: 'RETURN_POOL', waybillId: row.waybillId })
  if (!r.success) {
    ElMessage.error(r.message || '回池失败')
    return
  }
  ElMessage.success('已回自由池')
  await refreshAfterAct()
}

const onCancel = async (row: WaybillApi.CapacityWaybillItem) => {
  await ElMessageBox.confirm(`取消运单 ${row.waybillId}？`, '取消确认', { type: 'warning' })
  const r = await WaybillApi.waybillOpsAct({ action: 'CANCEL', waybillId: row.waybillId })
  if (!r.success) {
    ElMessage.error(r.message || '取消失败')
    return
  }
  ElMessage.success('已取消')
  detailVisible.value = false
  await refreshAfterAct()
}

const onCompensate = async (row: WaybillApi.CapacityWaybillItem) => {
  const { value } = await ElMessageBox.prompt(
    '赔付金额（元）。将建应付工单台账并记骑手欠款；可选取消运单。',
    '人工赔付',
    { inputPattern: /^\d+(\.\d{1,2})?$/, inputErrorMessage: '请输入金额', inputValue: '10' }
  )
  let cancelWaybill = false
  try {
    await ElMessageBox.confirm('是否同时强制取消该运单？', '取消运单', {
      distinguishCancelAndClose: true,
      confirmButtonText: '取消运单',
      cancelButtonText: '仅建工单'
    })
    cancelWaybill = true
  } catch (e: any) {
    if (e === 'close') return
    cancelWaybill = false
  }
  const r = await CompensateApi.createCompensate({
    waybillId: row.waybillId,
    amount: Number(value),
    reason: '客服人工赔付',
    cancelWaybill
  })
  if (!r.success) {
    ElMessage.error(r.message || '建单失败')
    return
  }
  ElMessage.success(r.message || '已建赔付工单')
  await refreshAfterAct()
}

const load = async () => {
  const r = await WaybillApi.getWaybillList({
    keyword: filters.keyword.trim() || undefined,
    city: filters.city || undefined,
    status: filters.status || undefined,
    strategy: filters.strategy || undefined,
    orgId: filters.orgId.trim() || undefined,
    pageNo: pageNo.value,
    pageSize: pageSize.value,
  })
  list.value = r?.list || []
  listTotal.value = r?.total || 0
}

const loadTodos = async () => {
  const r = await WaybillApi.getWaybillTodos({
    pageNo: todosPageNo.value,
    pageSize: todosPageSize.value,
  })
  todos.value = r?.list || []
  todosTotal.value = r?.total || 0
}

const onTabChange = (name: string | number) => {
  const next = name === 'todos' ? 'todos' : 'all'
  router.replace({ query: { ...route.query, tab: next } })
  if (next === 'todos') loadTodos()
}

function syncTabFromQuery() {
  tab.value = String(route.query.tab || '') === 'todos' ? 'todos' : 'all'
}

watch(
  () => route.query.tab,
  () => syncTabFromQuery()
)

onMounted(async () => {
  syncTabFromQuery()
  await Promise.all([load(), loadTodos()])
})
</script>
